import React, { CSSProperties, useState } from 'react';
import { ExerciseImage } from 'components/ExerciseImage';
import styled from 'styled-components';
import { P2 } from 'styleguide/Texts';
import { MuscleGroup } from 'hevy-shared';
import { CustomExerciseChicklet } from './CustomExerciseChicklet';
import { colors } from 'styleguide/colors';
import { themeState } from 'styleguide/theme';
import { muscleGroupFilterToHuman } from 'exercises/utils';
import { Spacing } from 'styleguide/spacing';
import { MicroIcon } from 'styleguide/Icons';
import { CircleView } from 'styleguide/CircleView';
import { FlexRow } from 'styleguide/Row';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${Spacing.sm}px;
  cursor: pointer;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 100%;
  gap: ${Spacing.sm}px;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

interface AddExerciseCellProps {
  image?: string;
  title: string;
  muscleGroup: MuscleGroup;
  isCustom: boolean;
  hidePlusIcons?: boolean;
  onClick: () => void;
  highlight: boolean;
  style?: CSSProperties;
}

export const AddExerciseCell = ({
  image,
  title,
  muscleGroup,
  isCustom,
  hidePlusIcons,
  highlight,
  onClick,
  style,
}: AddExerciseCellProps) => {
  const [wasRecentlyAdded, setWasRecentlyAdded] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  const backgroundColor = () => {
    if (highlight) {
      return themeState.current.name === 'light' ? colors.greyUltraLight : colors.greyDark;
    }

    if (isHovering) {
      return themeState.current.name === 'light' ? colors.greyHiperLight : colors.greyUltraDark;
    }
  };

  return (
    <Container
      style={{
        ...{
          backgroundColor: backgroundColor(),
        },
        ...style,
      }}
      onClick={() => {
        onClick();
        setWasRecentlyAdded(true);
        setTimeout(() => {
          setWasRecentlyAdded(false);
        }, 1000);
      }}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <ContentContainer>
        {!hidePlusIcons && (
          <CircleView
            diameter={24}
            style={{
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: colors.primary,
            }}
          >
            <MicroIcon type={wasRecentlyAdded ? 'check' : 'plus'} tint={colors.white} />
          </CircleView>
        )}

        <ExerciseImage source={image} diameter={48} />
        <TextContainer>
          <P2>{title}</P2>
          <FlexRow style={{ gap: Spacing.sm }}>
            <P2 type="secondary">{muscleGroupFilterToHuman(muscleGroup)}</P2>
            {isCustom && <CustomExerciseChicklet />}
          </FlexRow>
        </TextContainer>
      </ContentContainer>
    </Container>
  );
};
